import { useState, JSX, useEffect } from 'react';
import { Box, Card, CardContent } from '@mui/material';
import type {
  CompetitionOnboardingStatus,
  CompetitionOnboardingStatusEnum,
} from 'common/@types/recommendations';
import { Screener } from './steps/Screener';
import { Student } from 'common/hooks/useStudentSelection';
import { type GradeOption, PickGrade } from './steps/PickGrade';
import { MapTest } from './steps/MapTest';
import { PutCompetitionOnboardingStatusInput } from 'common/utils/amplify/putCompetitionOnboardingStatus';
import { getGradeObject } from './utils/grade-mappings';

type CompetitionOnboardingWizardProps = {
  competitionStatus?: CompetitionOnboardingStatus;
  student: Student;
  saveOnboardingStatus: (input: PutCompetitionOnboardingStatusInput) => Promise<void>;
  MapDetails?:
    | {
        studentId: string;
        name: string;
        email: string;
        age_grade: number;
      }
    | undefined;
};

const STEP_ORDER: CompetitionOnboardingStatusEnum[] = [
  'pick_grade',
  'pre_map_recommendations',
  'map',
  'recommendations',
];

export default function CompetitionOnboardingWizard({
  competitionStatus,
  student,
  saveOnboardingStatus,
  MapDetails,
}: CompetitionOnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState<CompetitionOnboardingStatusEnum | null>(null);
  const [activeStepComponent, setActiveStepComponent] = useState<JSX.Element>();
  const [gradeOptions, setGradeOptions] = useState<GradeOption[]>([]);

  const advanceStep = (grade?: number) => {
    if (!currentStep || !competitionStatus) return;

    let nextStep: CompetitionOnboardingStatusEnum | null = null;
    const nextStepIndex = STEP_ORDER.indexOf(currentStep) + 1;
    if (nextStepIndex < STEP_ORDER.length) {
      nextStep = STEP_ORDER[nextStepIndex];

      const saveProgressInput: PutCompetitionOnboardingStatusInput = {
        student_id: competitionStatus.full_student_id,
        onboarding_stage: nextStep,
        competition: competitionStatus.competition,
      };

      if (grade !== undefined) saveProgressInput.competition_grade = grade;

      saveOnboardingStatus(saveProgressInput);
    }
  };

  const previousStep = () => {
    if (!competitionStatus) return;
    saveOnboardingStatus({
      student_id: competitionStatus?.full_student_id,
      onboarding_stage: 'pick_grade',
      competition: competitionStatus?.competition,
    });
  };

  const selectGrade = (grade?: number) => {
    if (grade === undefined) return;
    advanceStep(grade);
  };

  const stepComponentMappings = {
    screener: <Screener student={student} advanceStep={advanceStep} />,
    pick_grade: (
      <PickGrade
        student={student}
        competitionStatus={competitionStatus}
        gradeOptions={gradeOptions}
        selectGrade={selectGrade}
        previousStep={previousStep}
      />
    ),
    pre_map_recommendations: <>Pre Map Recommendations</>, // User should not be able to see this since, he'd be on the recs page directly, this is only for all states to be mapped to prevent typescript errors
    map: <MapTest advanceStep={advanceStep} mapDetails={MapDetails} />,
    recommendations: <>Recommendations</>, // User should not be able to see this since, he'd be on the recs page directly, this is only for all states to be mapped to prevent typescript errors
  };

  useEffect(() => {
    if (!competitionStatus?.onboarding_stage) return;
    console.log(`competitionStatus: ${JSON.stringify(competitionStatus)}`);
    setCurrentStep(competitionStatus.onboarding_stage);
    setActiveStepComponent(stepComponentMappings[competitionStatus.onboarding_stage]);
  }, [competitionStatus?.onboarding_stage]);

  useEffect(() => {
    if (!competitionStatus?.original_age_grade) return;

    const startGrade = Math.max(-1, competitionStatus.original_age_grade - 2);
    const endGrade = Math.min(12, competitionStatus.original_age_grade + 2);

    const options = [];
    for (let i = startGrade; i <= endGrade; i++) {
      const gradeObject = getGradeObject(i.toString());
      if (!gradeObject) return;
      options.push({ value: i, label: gradeObject.shortName });
    }
    setGradeOptions(options);
  }, [competitionStatus?.original_age_grade]);

  useEffect(() => {
    if (currentStep) {
      setActiveStepComponent(stepComponentMappings[currentStep]);
    }
  }, [currentStep]);

  return (
    <Box sx={{ width: '100%' }}>
      <Card>
        <CardContent>{activeStepComponent}</CardContent>
      </Card>
    </Box>
  );
}
